/**
 * Hook useIsAT1Message
 *
 * Détermine si un message est un AT1 automatisé (premier message généré par l'IA).
 *
 * Conditions d'identification:
 * 1. Feature flag enabled: isAbMaturityLatencyExperimentEnabled
 * 2. Dossier dans l'expérience: maturityExperiments?.isLatencyTest
 * 3. Message envoyé par un agent: message.isFromAgent
 */

/**
 * ============================================================================
 * AB TEST: Maturity AT1 Feedback Experiment (ab_at1_feedback)
 * PR: #2501
 *
 * This file is part of an A/B test experiment for collecting user feedback
 * on automatically generated AT1 messages. When the experiment concludes,
 * this entire file should be either:
 * - Removed if the experiment is unsuccessful
 * - Renamed (remove AB_ prefix) and integrated permanently if successful
 *
 * To find all AB test code: grep -r "ab_at1_feedback" src/
 * ============================================================================
 */

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useGetDossierData } from '../../../../resources/dossier/queries';
import { useIsFirstAgentMessage } from '../../../../resources/discussion/queries';
import { Message } from '../../../../resources/discussion/types';
import { DossierId } from '../../../../resources/dossier/types';
import { getFeatureFlag } from '../../../../shared/feature-flags/getFeatureFlag';

export const useIsAT1Message = (message: Message): boolean => {
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const { dossier } = useGetDossierData(dossierId) || {};
    const { project } = dossier || {};
    const { maturityExperiments } = project || {};

    const isAbMaturityLatencyExperimentEnabled = getFeatureFlag('abMaturityLatencyExperiment');
    const isFirstAgentMessage = useIsFirstAgentMessage(dossierId, message.id);

    return useMemo(() => {
        // Doit être un message envoyé par un agent
        if (!message.isFromAgent) return false;

        // Feature flag doit être activé
        if (!isAbMaturityLatencyExperimentEnabled) return false;

        // Dossier doit être dans l'expérience AT1
        if (!maturityExperiments?.isLatencyTest) return false;

        // Doit être le premier message de l'agent
        if (!isFirstAgentMessage) return false;

        return true;
    }, [
        message.isFromAgent,
        isAbMaturityLatencyExperimentEnabled,
        maturityExperiments?.isLatencyTest,
        isFirstAgentMessage,
    ]);
};
